import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { agentStatusMeta } from '../utils/agentStatus';
import {
  MessageSquare,
  FileText,
  MousePointer,
  BookOpen,
  Dumbbell,
  Users,
  Plug,
  Eye,
  Clock,
} from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  description?: string;
  status: string;
}

interface TeachingMode {
  key: string;
  label: string;
  icon: React.ElementType;
  available: boolean;
  description: string;
}

const TEACHING_MODES: TeachingMode[] = [
  { key: 'dialogue', label: '对话教学', icon: MessageSquare, available: true, description: '通过多轮对话示教 Agent' },
  { key: 'documents', label: '文档教学', icon: FileText, available: true, description: '上传文档并提取知识' },
  { key: 'action', label: '操作教学', icon: MousePointer, available: false, description: '录制界面操作并生成执行流程' },
  { key: 'case', label: '案例教学', icon: BookOpen, available: false, description: '通过典型案例训练判断逻辑' },
  { key: 'training', label: '训练教学', icon: Dumbbell, available: false, description: '批量样本训练与微调' },
  { key: 'discussion', label: '讨论教学', icon: Users, available: false, description: '多角色讨论生成决策策略' },
  { key: 'interface', label: '接口教学', icon: Plug, available: false, description: '通过 API 调用示例示教' },
  { key: 'shadow', label: '影子教学', icon: Eye, available: false, description: '旁路运行并收集差距样本' },
];

export default function TeachingCenter() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    apiClient
      .get('/agents')
      .then((res) => setAgents(res.data))
      .catch((e) => console.error('Failed to load agents:', e))
      .finally(() => setLoading(false));
  }, []);

  const handleModeClick = (agentId: string, mode: TeachingMode) => {
    if (mode.available) {
      navigate(`/agents/${agentId}/teaching/${mode.key}`);
    } else {
      toast.info(`${mode.label} 即将推出，敬请期待`);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>示教中心</h1>
      <p className="text-muted mb-4">选择 Agent 与教学模式，通过专家表达蒸馏 Agent 能力</p>

      {/* 教学模式图例 */}
      <div className="card mb-4">
        <h3 style={{ margin: '0 0 12px' }}>八种教学模式</h3>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 12 }}>
          {TEACHING_MODES.map((mode) => {
            const Icon = mode.icon;
            return (
              <div
                key={mode.key}
                className="flex items-start gap-3"
                style={{ padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--bg-primary)', minWidth: 0 }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-md)',
                    background: mode.available ? 'rgba(126, 231, 135, 0.12)' : 'rgba(148, 163, 184, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={18} color={mode.available ? 'var(--color-success)' : 'var(--text-muted)'} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <span>{mode.label}</span>
                    {!mode.available && (
                      <span className="badge secondary" style={{ fontSize: 10 }}>即将推出</span>
                    )}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 2, wordBreak: 'break-word' }}>{mode.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 多 Agent 协作房间入口 */}
      <div className="card mb-4">
        <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div className="flex items-start gap-3">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                background: 'rgba(88, 166, 255, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Users size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 15 }}>多 Agent 协作房间</h3>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                让多个 Agent 以评审 / 分发 / 圆桌模式协作完成任务，观察并沉淀团队工作流
              </div>
            </div>
          </div>
          <button className="primary" onClick={() => navigate('/teaching/collab')}>
            <Users size={14} /> 进入协作房间
          </button>
        </div>
      </div>

      {agents.length === 0 ? (
        <div className="empty">
          <h3>暂无 Agent</h3>
          <button className="primary mt-2" onClick={() => navigate('/agents/create')}>
            新建 Agent
          </button>
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 16 }}>
          {agents.map((agent) => {
            const statusMeta = agentStatusMeta(agent.status);
            return (
              <div key={agent.id} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="flex items-center justify-between mb-2" style={{ gap: 8 }}>
                  <h3 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{agent.name}</h3>
                  {statusMeta ? (
                    <span className={`badge ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
                  ) : (
                    <span className={`badge ${agent.status === 'active' ? 'success' : 'info'}`}>{agent.status}</span>
                  )}
                </div>
              <p className="text-muted text-sm mb-3" style={{ wordBreak: 'break-word' }}>{agent.description || '暂无描述'}</p>
              <div className="flex flex-wrap gap-2" style={{ marginTop: 'auto' }}>
                {TEACHING_MODES.map((mode) => {
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.key}
                      className="secondary"
                      style={{
                        padding: '6px 10px',
                        fontSize: 13,
                        opacity: mode.available ? 1 : 0.7,
                        maxWidth: '100%',
                      }}
                      title={mode.description}
                      onClick={() => handleModeClick(agent.id, mode)}
                    >
                      <Icon size={14} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mode.label}</span>
                      {!mode.available && <Clock size={12} style={{ flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
